import { Widget } from "@foretagsplatsen/widgetjs";
import BaseDropdownWidget from "../BaseDropdownWidget/BaseDropdownWidget.js";
import EnsureStyleOnEnterAction from "../../../models/wysiwygEditor/actions/EnsureStyleOnEnterAction.js";
import HistoryAction from "../../../models/wysiwygEditor/actions/HistoryAction.js";
import LinkAction from "../../../models/wysiwygEditor/actions/LinkAction.js";
import ListsAction from "../../../models/wysiwygEditor/actions/ListsAction.js";
import MenuAction from "../../../models/wysiwygEditor/actions/MenuAction.js";
import PlaceholderAction from "../../../models/wysiwygEditor/actions/PlaceholderAction.js";
import StrikeAction from "../../../models/wysiwygEditor/actions/StrikeAction.js";
import UnderlineAction from "../../../models/wysiwygEditor/actions/UnderlineAction.js";
import wrapAsProseMirrorDropdown from "./wrapAsProseMirrorDropdown.js";
import WysiwygEditor from "../../../models/wysiwygEditor/WysiwygEditor.js";

/**
 * Rich Text editor widget.
 *
 * @param {string} content - Content of the editor
 * @param {function} onChange - Function called when the editor has
 * changed. It takes the editor content as argument
 * @param {PeriodReportDefaultText[]} defaultTexts - List of default text that can
 *   be inserted in the editor
 * @param {boolean} [forceHTML] - When true, the editor output changes as HTML,
 *   regardless of the mode (AST/HTML)
 * @param {boolean} [updateContentOnUpdate] - When true and `content` is a function,
 *   the content of the editor will be refreshed with the result of `content` each time
 *   this widget is updated
 * @param {InputValueActionBuilder} [inputValueActionBuilder] - Builder for InputValueAction passed
 *   to the WYSIWYG editor
 * @param {AbstractAction[]} [actions] - List of editor actions to add to the
 *   editor. By default, the editor has a collection of ready-to-use actions.
 *   If you want to add on top of the default ones, please use `extraActions`
 *   instead of duplicating the list of default actions to avoid de-sync
 * @param {string} [spec.name] - This string is added to the
 *   DOM as a "name" attribute. This is used by the form
 * @param {AbstractAction[]} [extraActions] - List of extra actions to add on top of the
 *   default one. If you want to change totally the available actions, you should use
 *   `actions` instead. This parameter is not used if `actions` is already provided
 */
export default class WysiwygEditorWidget extends Widget {
	constructor({
		content,
		onChange,
		defaultTexts,
		forceHTML = true,
		updateContentOnUpdate = false,
		inputValueActionBuilder,
		extraActions = [],
		actions,
		name,
	} = {}) {
		super(...arguments);

		this._content = content;
		this._updateContentOnUpdate = updateContentOnUpdate;

		this._editor = new WysiwygEditor({
			content,
			onChange,
			parentNode: () => this._getEditorContainer(),
			actions:
				actions ||
				this._defaultActions({
					defaultTexts,
					inputValueActionBuilder,
					extraActions,
				}),
			forceHTML,
			dropdownMenuItemBuilder: wrapAsProseMirrorDropdown({
				makeDropdown: (options) => new BaseDropdownWidget(options),
			}),
		});

		this._name = name;
	}

	focus() {
		this._editor.focus();
	}

	renderContentOn(html) {
		html.div({
			klass: "prosemirror-container",
			"data-test": "prosemirror-editor",
			name: this._name,
		});
	}

	replaceWholeTextWithHTML(text) {
		return this._editor.replaceWholeTextWithHTML(text);
	}

	replaceWholeTextWithASTNode(astNode) {
		return this._editor.replaceWholeTextWithASTNode(astNode);
	}

	replaceSelectionWithHTML(text) {
		return this._editor.replaceSelectionWithHTML(text);
	}

	replaceSelectionWithASTNode(node) {
		return this._editor.replaceSelectionWithASTNode(node);
	}

	updateContent() {
		this._editor.updateContent();
	}

	update() {
		// The super call is omitted on purpose, as this widget doesn't render anything
		// beside a jQuery container.
		if (!this._updateContentOnUpdate) {
			return;
		}

		this._editor.updateContent();
	}

	_defaultActions({ defaultTexts, inputValueActionBuilder, extraActions }) {
		return [
			new LinkAction(),
			new StrikeAction(),
			new UnderlineAction(),
			new ListsAction(),
			new MenuAction(),
			new HistoryAction(),
			...this._makeInputValueActions({
				defaultTexts,
				inputValueActionBuilder,
			}),
			new PlaceholderAction(),
			new EnsureStyleOnEnterAction(),
			...extraActions,
		];
	}

	_makeInputValueActions({ defaultTexts, inputValueActionBuilder }) {
		if (!inputValueActionBuilder) return [];

		return inputValueActionBuilder.build({
			extraParameters: { defaultTexts },
		});
	}

	_willDetach() {
		this._editor.tearDown();
	}

	_didAttach() {
		setTimeout(() => {
			this._editor.update();
		}, 0);
	}

	_getEditorContainer() {
		return this.asJQuery().find(".prosemirror-container").get(0);
	}
}
